import React from 'react';
import { connect, ConnectedProps } from '@cerebral/react';
import { state, sequences } from 'app/pwa/app.cerebral';
import LazyImage from 'common/components/LazyImage';

import { Wrapper, Spacer, ListWrapper, Avatar } from './elements';
import MSB002 from 'dc/MSB002';
import { OverlapIcons } from 'dc/OverlapIcons';
import { useText, useFormatDate, useGetAvatarUrl } from 'common/hooks';
import { DATE_FORMAT } from 'resources/constants';

const deps = {
  teamMembers: state.challenge.teamMembers,
  profiles: state.appContainer.profiles,
  isTeamProfilesListExpanded: state.challenge.info.isTeamProfilesListExpanded,
  toggleExpandedTeamProfilesList: sequences`challenge.info.toggleExpandedTeamProfilesList`,
};

export function MSB001({
  teamMembers,
  profiles,
  isTeamProfilesListExpanded,
  toggleExpandedTeamProfilesList,
}: typeof deps & ConnectedProps): React.ReactElement {
  const t = useText();
  const formatDate = useFormatDate();
  const getAvatarUrl = useGetAvatarUrl();

  function renderList() {
    const list = Object.keys(teamMembers).map(profileKey => {
      const profile = profiles[profileKey];

      if (!profile) return null;

      return (
        <LazyImage
          key={profileKey}
          onLoading={() => (
            <MSB002
              key={profileKey}
              caption={`${t('challenge.FeC1.attending')} ${formatDate(
                teamMembers[profileKey]?.joinedDatetime,
                DATE_FORMAT.DAY_MONTH_SHORT
              )}`}
              userName={profile.name}
              loadingImage
            />
          )}
          onLoaded={imageUrl => (
            <MSB002
              avatarImage={imageUrl}
              key={profileKey}
              caption={`${t('challenge.FeC1.attending')} ${formatDate(
                teamMembers[profileKey]?.joinedDatetime,
                DATE_FORMAT.DAY_MONTH_SHORT
              )}`}
              userName={profile.name}
            />
          )}
          path={getAvatarUrl(profile).small}
        />
      );
    });

    return (
      <ListWrapper>
        {list}
        <Spacer />
      </ListWrapper>
    );
  }

  const profileKeys = Object.keys(teamMembers);

  const avatarUrls = profileKeys
    .filter(profileKey => profiles[profileKey])
    .map(profileKey => getAvatarUrl(profiles[profileKey]).small);

  return (
    <>
      <Wrapper
        $expanded={isTeamProfilesListExpanded}
        onClick={() => toggleExpandedTeamProfilesList()}
      >
        {teamMembers && (
          <OverlapIcons
            iconUrls={avatarUrls}
            expanded={isTeamProfilesListExpanded}
            render={(avatarUrl, avatarPosition) => (
              <LazyImage
                key={avatarUrl}
                path={avatarUrl}
                onLoading={() => (
                  <Avatar $position={avatarPosition} isLoading />
                )}
                onLoaded={url => (
                  <Avatar $position={avatarPosition} image={url} />
                )}
              />
            )}
          />
        )}
      </Wrapper>
      {isTeamProfilesListExpanded ? renderList() : null}
    </>
  );
}

export default connect(deps, MSB001);
